<template>
  <BaseModal
    :is-open="isOpen"
    size="full"
    close-on-esc-key-click
    mobile-full-screen-modal
    @close="close"
  >
    <template #title>
      <div class="text-center truncate mx-12" v-html="title" />
    </template>

    <div ref="mapElement" class="map-modal w-screen sm:mt-4 sm:w-full ">
      Loading map...
    </div>
  </BaseModal>
</template>

<script setup>
import { nextTick, onMounted, ref, watch } from 'vue';

import BaseModal from '@/components/Ui/Modals/BaseModal.vue';
import useCustomBreakpoints from '@/utils/custom-breakpoints';
import waitFor from '@/utils/wait-for';

let historyHelper;

const props = defineProps({
  mapConfig: {
    type: Object,
    required: true,
    validator(value) {
      return value.location_latitude && value.location_longitude;
    },
  },
  title: {
    type: String,
    required: true,
  },
});

const isOpen = ref(false);
const mapElement = ref(null);

const { isMobile } = useCustomBreakpoints();

const loadGoogleMap = () => {
  const lat = parseFloat(props.mapConfig.location_latitude);
  const lng = parseFloat(props.mapConfig.location_longitude);

  window.map = new window.google.maps.Map(mapElement.value, {
    mapId: '179e566d0aeca7f7',
    gestureHandling: 'greedy',
    disableDefaultUI: true,
    zoomControl: !isMobile.value,
    zoomControlOptions: {
      style: window.google.maps.ZoomControlStyle.LARGE,
      position: window.google.maps.ControlPosition.RIGHT_BOTTOM,
    },
    streetViewControl: true,
    streetViewControlOptions: {
      position: window.google.maps.ControlPosition.RIGHT_BOTTOM,
    },
    center: { lat, lng },
    zoom: 14,
  });

  const windowScale = window.visualViewport.scale || 1; // scale for zoomed in mobile devices
  const iconXSize = 36 / windowScale;
  const iconYSize = 52 / windowScale;
  const iconAnchor = 18 / windowScale;

  // eslint-disable-next-line no-new
  new window.google.maps.Marker({
    position: { lat, lng },
    map: window.map,
    icon: {
      url: '/img/icons/pin-red.svg',
      size: new window.google.maps.Size(iconXSize, iconYSize),
      scaledSize: new window.google.maps.Size(iconXSize, iconYSize),
      anchor: new window.google.maps.Point(iconAnchor, iconYSize),
    },
  });
};

const loadGoogleMapsScript = () => {
  if (!window.google || !window.google.maps) {
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?v=3.65&loading=async&libraries=marker&language=en&key=${window.GOOGLE_MAPS_API_KEY}`;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      waitFor(() => {
        return window.google?.maps?.Map;
      }, loadGoogleMap);
    };
    document.head.appendChild(script);
  } else {
    loadGoogleMap();
  }
};

watch(
  () => isOpen.value,
  (newVal) => {
    if (newVal) {
      nextTick(loadGoogleMapsScript);
    }
  },
  { immediate: true },
);

const initHistoryHelper = () => {
  historyHelper = App.components.history_state({
    onChange(popEvent) {
      if (popEvent?.state === 'map') {
        isOpen.value = true;
      } else {
        isOpen.value = false;
      }
    },
  });
};

const close = () => {
  if (isOpen.value) {
    historyHelper.back();
  }
};

const open = () => {
  isOpen.value = true;
  historyHelper.pushState('map', props.title, '#map');
};

onMounted(() => {
  initHistoryHelper();

  window.triggerMapModal = open;

  if (window.location.hash === '#map') {
    window.history.replaceState(null, null, ' ');
    open();
  }
});
</script>

<style lang="scss" scoped>
@use "@sass/base/variables";

:deep(.ds-modal) {
  @media #{variables.$for-medium-and-up} {
    max-width: 1280px;
    width: 100%;
  }
  @media #{variables.$for-large-and-up} {
    max-width: 1680px;
    width: 100%;
  }
}

.map-modal {
  height: calc(100vh - 160px - 150px);

  @media (max-width: variables.$screen-xs-max) {
    margin: -30px -24px;
    height: calc(100vh - 64px);
  }
}
</style>
